/**
 * How high the runner was along the stretch of track the camera is passing now. The camera trails the
 * runner by a few meters: a walkable top the runner ran along (pipe crown, container roof) is ground
 * for the camera when it gets there – even after the runner has dropped off the end – while a pipe the
 * runner ran through stays a ceiling the camera glides under (terrainQueries: topReach).
 *
 * Samples are kept per distance bucket (frame-rate independent): the highest feet height the runner had
 * in each `spacing` m slice of the last `length` m.
 */
export class RunnerHeightTrail {
  private readonly heights: Float64Array;
  /** Bucket index held by each slot (NaN = empty) – the ring is indexed by distance. */
  private readonly buckets: Float64Array;

  constructor(
    /** Stretch behind the runner that is remembered (m) – longer than the camera trails behind. */
    length: number,
    /** Bucket size along the track (m). */
    private readonly spacing: number,
  ) {
    const slots = Math.ceil(length / spacing) + 1;
    this.heights = new Float64Array(slots);
    this.buckets = new Float64Array(slots).fill(Number.NaN);
  }

  /** Records the runner's feet height at a track distance (every rendered frame). */
  record(distance: number, height: number): void {
    const bucket = Math.floor(distance / this.spacing);
    const slot = this.slotOf(bucket);
    if (this.buckets[slot] !== bucket) {
      this.buckets[slot] = bucket;
      this.heights[slot] = height;
    } else {
      this.heights[slot] = Math.max(this.heights[slot] ?? height, height);
    }
  }

  /** Highest feet height the runner had between the two track distances, or null if it left no sample there. */
  highestIn(fromDistance: number, toDistance: number): number | null {
    let highest: number | null = null;
    const first = Math.floor(fromDistance / this.spacing);
    const last = Math.min(Math.floor(toDistance / this.spacing), first + this.heights.length - 1);
    for (let bucket = first; bucket <= last; bucket++) {
      const slot = this.slotOf(bucket);
      if (this.buckets[slot] !== bucket) continue;
      const height = this.heights[slot] ?? 0;
      highest = highest === null ? height : Math.max(highest, height);
    }
    return highest;
  }

  /**
   * topReach for terrain queries along the camera's path (terrainQueries): the runner's highest feet height
   * there + one step – or its current height where it left no sample (the camera behind the start line).
   */
  topReachIn(fromDistance: number, toDistance: number, currentHeight: number, maxStepHeight: number): number {
    return (this.highestIn(fromDistance, toDistance) ?? currentHeight) + maxStepHeight;
  }

  /** Forgets everything (new run: distances start over). */
  clear(): void {
    this.buckets.fill(Number.NaN);
  }

  private slotOf(bucket: number): number {
    const slots = this.heights.length;
    return ((bucket % slots) + slots) % slots;
  }
}
